import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { hubApi } from "../services/hubApi";

const webhookEvents = [
  { key: "call:incoming", label: "Chamada entrante" },
  { key: "call:answered", label: "Chamada atendida" },
  { key: "call:ended", label: "Chamada finalizada" },
  { key: "call:missed", label: "Chamada perdida" },
  { key: "call:recording", label: "Gravacao disponivel" },
  { key: "call:transferred", label: "Chamada transferida" },
];

export default function WebhookConfig({ connectionId }) {
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const loadConfig = useCallback(async () => {
    if (!connectionId) return;
    setLoading(true);
    try {
      const { data } = await hubApi.voipGetConfig(connectionId);
      const config = data.config || {};
      setUrl(config.webhookUrl || "");
      setEvents(config.webhookEvents || []);
    } catch (err) {
      console.error("Error loading webhook config:", err);
    } finally {
      setLoading(false);
    }
  }, [connectionId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleToggleEvent = (eventKey) => {
    setEvents((prev) =>
      prev.includes(eventKey)
        ? prev.filter((e) => e !== eventKey)
        : [...prev, eventKey]
    );
    setSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await hubApi.voipUpdateConfig(connectionId, {
        webhookUrl: url || null,
        webhookEvents: events,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao salvar webhook");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!url) {
      setTestResult({ success: false, error: "URL nao informada" });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      // Send a test payload to the webhook URL
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event: "test",
          timestamp: new Date().toISOString(),
          data: { message: "Teste de webhook OtimizAPP VoIP" },
        }),
      });
      setTestResult({
        success: response.ok,
        status: response.status,
        error: response.ok ? null : `HTTP ${response.status}`,
      });
    } catch (err) {
      setTestResult({ success: false, error: err.message });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CircularProgress size={28} sx={{ color: "#8B5CF6" }} />
      </Box>
    );
  }

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "#111827",
      borderRadius: "10px",
      "& fieldset": { borderColor: "rgba(255,255,255,0.08)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.15)" },
    },
    "& .MuiInputLabel-root": { color: "#6B7280" },
    "& .MuiOutlinedInput-input": { color: "#F3F4F6", fontSize: "0.85rem" },
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: "#F3F4F6", mb: 3 }}>
        Webhook
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          Webhook salvo com sucesso!
        </Alert>
      )}

      <Card
        sx={{
          bgcolor: "#1E293B",
          border: "1px solid rgba(255,255,255,0.04)",
          borderRadius: "14px",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* URL */}
          <Typography
            variant="subtitle2"
            sx={{
              color: "#9CA3AF",
              mb: 1.5,
              fontWeight: 600,
              textTransform: "uppercase",
              fontSize: "0.7rem",
              letterSpacing: "0.5px",
            }}
          >
            URL do Webhook
          </Typography>

          <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
            <TextField
              fullWidth
              size="small"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setSuccess(false);
              }}
              placeholder="https://seu-servidor.com/webhook/voip"
              sx={fieldSx}
            />
            <Button
              variant="outlined"
              onClick={handleTest}
              disabled={testing || !url}
              startIcon={
                testing ? <CircularProgress size={14} /> : <SendIcon />
              }
              sx={{
                borderColor: "rgba(255,255,255,0.15)",
                color: "#D1D5DB",
                "&:hover": { borderColor: "#8B5CF6", color: "#8B5CF6" },
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                minWidth: 100,
                fontSize: "0.8rem",
              }}
            >
              Testar
            </Button>
          </Box>

          {testResult && (
            <Box sx={{ mb: 2 }}>
              <Chip
                icon={
                  testResult.success ? (
                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <ErrorIcon sx={{ fontSize: 16 }} />
                  )
                }
                label={
                  testResult.success
                    ? `OK (HTTP ${testResult.status})`
                    : testResult.error || "Falhou"
                }
                size="small"
                sx={{
                  bgcolor: testResult.success
                    ? "rgba(37,211,102,0.12)"
                    : "rgba(239,68,68,0.12)",
                  color: testResult.success ? "#25D366" : "#EF4444",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  "& .MuiChip-icon": {
                    color: testResult.success ? "#25D366" : "#EF4444",
                  },
                }}
              />
            </Box>
          )}

          {/* Events */}
          <Typography
            variant="subtitle2"
            sx={{
              color: "#9CA3AF",
              mb: 1.5,
              fontWeight: 600,
              textTransform: "uppercase",
              fontSize: "0.7rem",
              letterSpacing: "0.5px",
            }}
          >
            Eventos
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 3 }}>
            {webhookEvents.map((evt) => (
              <FormControlLabel
                key={evt.key}
                control={
                  <Checkbox
                    checked={events.includes(evt.key)}
                    onChange={() => handleToggleEvent(evt.key)}
                    size="small"
                    sx={{
                      color: "#4B5563",
                      "&.Mui-checked": { color: "#8B5CF6" },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{ color: "#D1D5DB", fontSize: "0.85rem" }}
                    >
                      {evt.label}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#4B5563",
                        fontSize: "0.7rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {evt.key}
                    </Typography>
                  </Box>
                }
                sx={{ ml: 0 }}
              />
            ))}
          </Box>

          {/* Save */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                bgcolor: "#8B5CF6",
                "&:hover": { bgcolor: "#7C3AED" },
                borderRadius: "10px",
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              {saving ? "Salvando..." : "Salvar Webhook"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
